import React from 'react';
import './ChangeRequestIndicator.scss';
import _ from 'lodash';
import { YesNo } from '@/components/partials/ProductView/ProductMetadata/ProductMetadataCommon';
import type {
  ChangeRequestDisplayObject,
  ChangeRequestDisplayPrimitive,
  ChangeRequestDisplayValue,
} from '@/types/change-request-types';
import { useTranslation } from 'react-i18next';

interface ChangeRequestIndicatorProps {
  show: boolean | undefined;
  displayValue?: ChangeRequestDisplayValue;
  customObjectKeys?: string[];
  customSeparator?: string;
  customClass?: string;
  omitDisplayValue?: boolean;
}

const ChangeRequestIndicator = ({
  show,
  displayValue,
  customObjectKeys,
  customSeparator,
  customClass = '',
  omitDisplayValue = false,
}: ChangeRequestIndicatorProps) => {
  const { t } = useTranslation();
  const defaultSeparator = customSeparator ? customSeparator : ' - ';
  const emptyValue = t('common.generic.empty');

  const formatPrimitiveValue = (value: ChangeRequestDisplayPrimitive) => {
    if (_.isBoolean(value)) {
      return value ? t('product.yes') : t('product.no');
    }
    if (_.isNil(value) || value === '') {
      return emptyValue;
    }
    return String(value);
  };

  const formatObjectValue = (
    value: ChangeRequestDisplayObject,
    keys?: string[]
  ) => {
    const values = keys ? keys.map((key) => value[key]) : Object.values(value);
    return values.map(formatPrimitiveValue).join(defaultSeparator);
  };

  const getDisplayValue = () => {
    if (
      !_.isBoolean(displayValue) &&
      !_.isNumber(displayValue) &&
      (_.isNil(displayValue) || _.isEmpty(displayValue))
    ) {
      return <span className="tooltip-text">{emptyValue}</span>;
    }

    if (_.isBoolean(displayValue)) {
      return (
        <span className="tooltip-text">
          <YesNo value={displayValue} />
        </span>
      );
    }

    if (_.isArray(displayValue)) {
      if (_.every(displayValue, _.isPlainObject)) {
        const objectValues = displayValue as ChangeRequestDisplayObject[];

        return (
          <ul className="tooltip-list">
            {objectValues.map((item, index: number) => {
              const itemText = formatObjectValue(item, customObjectKeys);
              const itemId = item.id;
              const itemKey =
                _.isString(itemId) || _.isNumber(itemId)
                  ? `item-${itemId}`
                  : `item-${index}`;

              return (
                <li key={itemKey} className="display-object-list-item">
                  {itemText}
                </li>
              );
            })}
          </ul>
        );
      }

      if (_.every(displayValue, _.isString)) {
        const stringValues = displayValue as string[];

        return (
          <ul className="tooltip-list">
            {stringValues.map((value: string, index: number) => {
              return (
                <li className="tooltip-item" key={`tooltip-item-${index}`}>
                  {value}
                </li>
              );
            })}
          </ul>
        );
      }

      const itemText = displayValue.map((value) => {
        if (_.isPlainObject(value)) {
          return formatObjectValue(
            value as ChangeRequestDisplayObject,
            customObjectKeys
          );
        }
        return formatPrimitiveValue(value as ChangeRequestDisplayPrimitive);
      });
      return (
        <span className="tooltip-text">{itemText.join(defaultSeparator)}</span>
      );
    }

    if (_.isPlainObject(displayValue)) {
      const objectValue = displayValue as ChangeRequestDisplayObject;
      const itemText = formatObjectValue(objectValue, customObjectKeys);
      return <span className="tooltip-text">{itemText}</span>;
    }

    return <span className="tooltip-text">{String(displayValue)}</span>;
  };

  return (
    <>
      {show ? (
        <div className={`cr-change-indicator-container ${customClass}`}>
          {!omitDisplayValue ? (
            <div className={'popUp-container-tooltip'}>
              <>
                <div className={'tooltip-title'}>
                  {t('product.changeIndicator.originalValue').toUpperCase()}
                </div>
                {getDisplayValue()}
              </>
            </div>
          ) : null}
          <i
            className="icon far fa-edit cr-change-indicator"
            aria-hidden="true"
          ></i>
        </div>
      ) : null}
    </>
  );
};
export default ChangeRequestIndicator;
